import React, { useState, useEffect, useMemo } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { Form, Button, message, Select, Space } from "antd";
import Breadcrumb from "../../../components/Breadcrumb";
import IndexTransferElement from "../../../components/Application/DefaultIndexSettings/IndexTransferElement";
import IndexTable from "../../../components/Application/DefaultIndexSettings/IndexTableElement";
import "./style.css";
import { SolutionSlugs } from "../../../utils/solutions";
// get indexes api react query calls
import { useGetAllIndexes } from "../../../hooks/indexes.hooks";
// get solutions api react query calls
import {
  useGetAllSolutions,
  useGetDefaultIndex,
  useSetDefaultIndexes,
} from "../../../hooks/solutions.hook";

const { Option } = Select;

const itemStyles = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 6 },
    md: { span: 6 },
    lg: { span: 5 },
    xl: { span: 5 },
    xxl: { span: 5 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 18 },
    md: { span: 18 },
    lg: { span: 15 },
    xl: { span: 12 },
    xxl: { span: 12 },
  },
};

const LOCALE = {
  itemUnit: "Index",
  itemsUnit: "Indexes",
  notFoundContent: "No indexes",
  searchPlaceholder: "Search here",
};
const LISTSTYLE = {
  width: 400,
  height: 460,
};

const DefaultIndexSettings = () => {
  const { control, handleSubmit, reset, watch, setValue } = useForm({
    mode: "onChange",
  });
  const targetKeys = watch("indexIds");
  const { fields, replace } = useFieldArray({
    control,
    name: "customIndexes",
  });

  const [solutionID, setSolutionID] = useState(undefined);

  // Get Index Data
  const { data: IndexData } = useGetAllIndexes({
    select: (dataList) => {
      let MockData;
      if (dataList !== undefined) {
        MockData = dataList.sort((a, b) => a.name.localeCompare(b.name));
      }
      return MockData;
    },
    onError: () => message.error("Server Error"),
  });

  // Get Solution Data
  const { isLoading: isLoadingSolutionsData, data: SolutionsData } =
    useGetAllSolutions({
      select: (dataList) => {
        let MockData;
        if (dataList !== undefined) {
          MockData = dataList.sort((a, b) => a._id.localeCompare(b._id));
        }
        return MockData;
      },
      onError: () => message.error("Server Error"),
    });

  const SolutionsList = useMemo(() => {
    if (SolutionsData !== undefined) {
      return SolutionsData.sort((a, b) =>
        a.solution_slug.localeCompare(b.solution_slug),
      ).map((solution) => (
        <Option
          key={solution._id}
          value={solution._id}
          disabled={
            !SolutionSlugs.find(
              (solutionSlug) =>
                solutionSlug.solutionSlug === solution.solution_slug,
            )
          }
        >
          {solution.solution_name}
        </Option>
      ));
    }
  }, [SolutionsData]);

  // Get Default Index
  const { data: GetDefaultIndexData } = useGetDefaultIndex(solutionID, {
    onError: () => message.error("Server Error"),
  });

  useEffect(() => {
    const initialTargetKeys = GetDefaultIndexData?.map((item) => item.index_id);
    setValue("indexIds", initialTargetKeys);
  }, [GetDefaultIndexData]);
  useEffect(() => {
    // TODO: can be managed using useEffect for GetDefaultIndexData - not merged. two use effets are still available.
    const IndexMappingDataSet = IndexData?.filter(
      (index) => targetKeys?.includes(index._id) && index.editable === true,
    );

    const DefaultIndexDataSet = GetDefaultIndexData?.filter(
      (index) =>
        targetKeys?.includes(index.index_id) && index.custom_values === true,
    );
    if (
      IndexMappingDataSet !== undefined &&
      DefaultIndexDataSet !== undefined
    ) {
      const IndexMappingTable = IndexMappingDataSet.map((indexMappingData) => {
        const customIndexArray = DefaultIndexDataSet.find(
          (defaultIndexData) =>
            defaultIndexData.index_id === indexMappingData._id,
        );

        return customIndexArray?.custom_values_data ?? indexMappingData;
      });
      replace(IndexMappingTable);
    }
  }, [watch("indexIds")]);

  // reset function
  const resetFunction = () => {
    setSolutionID(undefined);
    reset();
  };

  // Submit Functions
  // Add Default Indexes
  const { mutate: mutateAddDefaultIndexes } = useSetDefaultIndexes({
    onSuccess: () => {
      message.success("Default Indexes Updated Successfully");
      resetFunction();
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });

  const onSubmit = (data) => {
    const { customIndexes, indexIds } = data;

    const defaultIndexes = indexIds.map((indexId) => {
      const customIndexArray = customIndexes.find(
        (customIndex) => customIndex._id === indexId,
      );

      return {
        index_id:
          customIndexArray?._id === indexId ? customIndexArray?._id : indexId,
        custom_values: customIndexArray?.custom_values ?? false,
        custom_values_data:
          customIndexArray?.custom_values === true
            ? customIndexArray
            : undefined,
      };
    });

    mutateAddDefaultIndexes({
      id: solutionID,
      data: { defaultIndexes },
    });
  };

  return (
    <div>
      <div className="container_aa">
        <h3>Default Index Settings</h3>
        <Breadcrumb
          routes={[
            { path: "/", breadcrumbName: "Dashboard" },
            {
              path: "default-index-settings",
              breadcrumbName: "Default Index Settings",
            },
          ]}
        />
      </div>
      <div className="container_ab">
        <div className="container_style_1_b">
          <form onSubmit={handleSubmit(onSubmit)}>
            <Form.Item {...itemStyles} label="Solution">
              <Select
                placeholder="Select a Solution "
                showSearch
                notFoundContent={null}
                loading={isLoadingSolutionsData}
                optionFilterProp="children"
                onChange={(values) => {
                  setSolutionID(values);
                }}
                style={{ width: "51%" }}
                value={solutionID}
                filterOption={(input, option) => {
                  return (
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  );
                }}
              >
                {SolutionsList}
              </Select>
            </Form.Item>
            <div className="container_style_defaultIndexSettings">
              <IndexTransferElement
                IndexData={IndexData}
                solutionID={solutionID}
                locale={LOCALE}
                listStyle={LISTSTYLE}
                control={control}
              />
              {fields.length > 0 && (
                <IndexTable
                  dataSource={fields}
                  watch={watch}
                  control={control}
                />
              )}
              <Form.Item
                wrapperCol={{ span: 12, offset: 10 }}
                style={{ marginTop: 15 }}
              >
                <Space>
                  <Button onClick={resetFunction}>Cancel</Button>

                  <Button
                    type="primary"
                    disabled={
                      targetKeys === undefined || targetKeys.length === 0
                    }
                    htmlType="submit"
                  >
                    Update
                  </Button>
                </Space>
              </Form.Item>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DefaultIndexSettings;
